import { t as translate } from "@/shared/i18n";
import * as React from "react";

export interface SEOProps {
  /** Page title, already translated. Slotted into `seo.titleTemplate`. */
  title?: string;
  description?: string;
  /** Absolute URL of the share image. */
  image?: string;
  /** Absolute canonical URL. Defaults to the current location. */
  canonical?: string;
  /** Keep private screens (My farm, Operations) out of search results. */
  noIndex?: boolean;
  type?: "website" | "article";
}

const setMeta = (
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

/**
 * Document title and social/meta tags. The public catalogue has to be
 * shareable and indexable (SPEC §9 — the reason this app uses BrowserRouter),
 * so every public page should render one of these.
 *
 * Deliberately dependency-free: react-helmet is more machinery than a handful
 * of head tags needs.
 */
const SEO = ({
  title,
  description,
  image,
  canonical,
  noIndex = false,
  type = "website",
}: SEOProps) => {
  React.useEffect(() => {
    const fullTitle = title
      ? translate("seo.titleTemplate", { title })
      : translate("seo.defaultTitle");
    const desc = description || translate("seo.defaultDescription");
    const url = canonical || window.location.href;

    document.title = fullTitle;

    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      desc
    );
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      image ? "summary_large_image" : "summary"
    );
    if (image) {
      setMeta('meta[property="og:image"]', "property", "og:image", image);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    }
    setMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noIndex ? "noindex, nofollow" : "index, follow"
    );
    setLink("canonical", url);
  }, [title, description, image, canonical, noIndex, type]);

  return null;
};

export { SEO };
